/**
 * `GameLogin` — the auth card. A player picks a GM-defined slot (a slot already held by
 * a live connection shows disabled, so a *different* player can't fresh-pick it; the same
 * player reconnects via the remembered token, which evicts the stale socket) and
 * enters the room password if one is set; a GM signs in with the host key. A thin view
 * over the app-wide {@link useGameSession}, which owns the slot list, the handshake, and
 * the remembered-token reconnect. Credential rejections (wrong password / host key) are
 * decided server-side at `POST /api/game/login` and surfaced here inline — never re-checked
 * in the client.
 *
 * Styled as the GMS//OS login terminal — the brutalist `gms-login-*` classes live in
 * `routes/home/gms-login.css`, which the `/login` window pulls in (white laminate, ink
 * borders, JetBrains Mono, GMS red). This card renders inside that window's `.gms-card`.
 */
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import { useState } from "react";
import * as Select from "@radix-ui/react-select";
import * as Tabs from "@radix-ui/react-tabs";
import type { GameKickedMessage, GameRejectReason } from "@lancer-vtt/shared";
import { useGameSessionContext } from "./GameSessionProvider";

const REJECT_COPY: Record<GameRejectReason, string> = {
  bad_password: "Incorrect room password.",
  unknown_slot: "That slot no longer exists.",
  bad_token: "Your omninet link expired. Select your slot again.",
  bad_host_key: "Incorrect GM host key.",
  missing_credentials: "Select a slot or enter the GM host key.",
};

/**
 * Copy for `RequireAuth`'s `/login` redirect after a `game_kicked` — see
 * {@link GameKickedMessage} for what each reason means server-side. Kept distinct from
 * `REJECT_COPY`: a kick evicted an *established* connection, a reject failed an *incoming*
 * hello, and the login card must say which actually happened.
 */
const KICK_COPY: Record<GameKickedMessage["reason"], string> = {
  removed: "The GM removed your seat. Select a slot to relink to the omninet.",
  evicted: "This slot relinked on another device — the link here went dark.",
};

export function GameLogin() {
  const session = useGameSessionContext();
  const [slotId, setSlotId] = useState("");
  const [password, setPassword] = useState("");
  const [hostKey, setHostKey] = useState("");

  const list = session.slotList;
  const playerSlots = list?.slots.filter((s) => s.role === "player") ?? [];
  const connecting = session.status === "connecting";

  return (
    <div className="gms-login-body">
      {session.kickReason && (
        <p className="gms-login-error gms-login-kick">{KICK_COPY[session.kickReason]}</p>
      )}
      {session.rejectReason && (
        <p className="gms-login-error">{REJECT_COPY[session.rejectReason]}</p>
      )}

      <Tabs.Root defaultValue="player">
        <Tabs.List className="gms-login-tabs">
          <Tabs.Trigger value="player" className="gms-login-tab">
            Player
          </Tabs.Trigger>
          <Tabs.Trigger value="gm" className="gms-login-tab">
            Game Master
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="player" className="gms-login-tabpanel">
          {list && playerSlots.length === 0 ? (
            <p className="gms-login-note">No player slots yet. Ask your GM to add one.</p>
          ) : (
            <form
              className="gms-login-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (slotId) session.joinSlot(slotId, password || undefined);
              }}
            >
              <Select.Root value={slotId} onValueChange={setSlotId} disabled={!list}>
                <Select.Trigger className="gms-login-select" aria-label="Pilot slot">
                  <Select.Value placeholder={list ? "Select a slot" : "Loading slots…"} />
                  <Select.Icon className="gms-login-field-icon">
                    <Icon icon={faChevronDown} />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content
                    position="popper"
                    sideOffset={4}
                    className="gms-login-menu"
                  >
                    <Select.Viewport>
                      {playerSlots.map((s) => (
                        <Select.Item
                          key={s.id}
                          value={s.id}
                          disabled={s.taken}
                          className="gms-login-option"
                        >
                          <Select.ItemText>
                            {s.name}
                            {s.taken ? " · in use" : ""}
                          </Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>

              {list?.passwordRequired && (
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Room password"
                  className="gms-login-field"
                />
              )}

              <button type="submit" disabled={!slotId || connecting} className="gms-login-btn">
                {connecting ? "Linking to omninet…" : "Join"}
              </button>
            </form>
          )}
        </Tabs.Content>

        <Tabs.Content value="gm" className="gms-login-tabpanel">
          <form
            className="gms-login-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (hostKey) session.joinAsGm(hostKey);
            }}
          >
            <input
              type="password"
              value={hostKey}
              onChange={(e) => setHostKey(e.target.value)}
              placeholder="GM host key"
              className="gms-login-field"
            />
            <button type="submit" disabled={!hostKey || connecting} className="gms-login-btn">
              {connecting ? "Connecting…" : "Sign in as GM"}
            </button>
          </form>
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}
